import { useEffect, useRef, useState } from "react";
import {
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
    DEFAULT_SEARCH_INPUT_LENGTH,
} from "../../../../constants/app";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";

interface SelectableItem {
  id: number;
  label: string;
  postcode?: string; // Optional property for suburbs
  search_type: string;
}

interface MultiSelectProps {
  field?: string;
  onSelectionChange: (selectedIds: string | string[]) => void;
  items: SelectableItem[];
  selectedItems: string[];
  accessRestriction?: number;
  onSearch?: (searchTerm: string) => void;
  isSuburb?: boolean; // Optional prop to indicate if the items are suburbs
}

export default function MultiSelect(props: MultiSelectProps) {
  const {
    onSearch,
    onSelectionChange,
    items,
    selectedItems,
    accessRestriction,
    isSuburb,
  } = props;
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [filteredItems, setFilteredItems] = useState<SelectableItem[]>(items);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [shouldTriggerSearch, setShouldTriggerSearch] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debouncedSearch = useDebouncedValue(
    inputValue,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update debounced keyword for suburb search
  useEffect(() => {
    if (!shouldTriggerSearch) {
      setShouldTriggerSearch(true);
      return;
    }
    
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      if (onSearch) {
        onSearch(debouncedSearch);
      }
    }
  }, [debouncedSearch]);

  const addTag = (suburb: string, keepDropdownOpen: boolean = false) => {
    if (!selectedTags?.includes(suburb)) {
      const updatedTags = [...selectedTags, suburb];
      setSelectedTags(updatedTags);
      onSelectionChange(updatedTags);
    }
    if (!keepDropdownOpen) {
      setInputValue("");
    }
    setDropdownOpen(true);
  };

  const toggleTag = (suburb: string) => {
    let updatedTags: string[];
    if (selectedTags?.includes(suburb)) {
      // Remove if already selected
      updatedTags = selectedTags?.filter((tag) => tag !== suburb);
    } else {
      // Add if not selected
      updatedTags = [...selectedTags, suburb];
    }
    setSelectedTags(updatedTags);
    onSelectionChange(updatedTags);
    setShouldTriggerSearch(false); // Prevent triggering search when clearing input
    setInputValue(""); // Clear the input after selection
    setDropdownOpen(true);
  };

  const removeTag = (label: string) => {
    const updatedTags = selectedTags?.filter((tag) => tag !== label);
    setSelectedTags(updatedTags);
    onSelectionChange(updatedTags);
    setDropdownOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setDropdownOpen(true);

    if (value === "") {
      // If input is cleared, show all items
      setFilteredItems(items);
    } else {
      // Filter items based on search
      const filtered = items.filter((item) =>
        item.label.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredItems(filtered);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && filteredItems.length > 0) {
      addTag(filteredItems[0].label);
      e.preventDefault();
    } else if (
      e.key === "Backspace" &&
      !inputValue &&
      selectedTags?.length > 0
    ) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  useEffect(() => {
    setSelectedTags(selectedItems);
  }, [selectedItems]);

  const focusInput = () => {
    inputRef.current?.focus();
    setDropdownOpen(true);
  };

  return (
    <div
      className={`position-relative border radius-8px p-10px ${
        dropdownOpen ? "border-grey-light" : "border-off-white"
      } ${accessRestriction === 1 ? "disable-div" : ""}`}
      ref={containerRef}
      onClick={focusInput}
      style={{ cursor: "text" }}
    >
      <div className="d-flex flex-wrap align-items-center gap-8 text-break">
        {selectedTags && selectedTags?.map((label) => (
          <span
            key={label}
            className="bg-teal-light py-3px ps-13px pe-9px radius-10px d-flex gap-4 align-items-center text-break"
          >
            <div className="f-16 fw-400 text-black">{isSuburb ? label.replace(/\s*-\s*\d{4}(?:\s*-\s*M)?$/, "") : label}</div>
            <svg
              className="min-w-24px cursor-pointer"
              onClick={() => removeTag(label)}
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.68867 6.81133C7.31703 6.18297 8.33579 6.18297 8.96414 6.81133L12.3773 10.2245L15.7906 6.81133C16.4189 6.18297 17.4377 6.18297 18.066 6.81133C18.6944 7.43968 18.6944 8.45844 18.066 9.0868L14.6528 12.5L18.066 15.9132C18.6944 16.5416 18.6944 17.5603 18.066 18.1887C17.4377 18.817 16.4189 18.817 15.7906 18.1887L12.3773 14.7755L8.96414 18.1887C8.33579 18.817 7.31703 18.817 6.68867 18.1887C6.06032 17.5603 6.06032 16.5416 6.68867 15.9132L10.1019 12.5L6.68867 9.0868C6.06032 8.45844 6.06032 7.43968 6.68867 6.81133Z"
                fill="black"
                stroke="#C0F2EE"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="border-0 flex-grow-1"
          placeholder={selectedTags.length === 0 ? `No suburbs selected` : `Type to search...`}
          style={{ outline: "none", minWidth: "100px" }}
          disabled={accessRestriction === 1}
        />
      </div>

      {dropdownOpen && filteredItems.length > 0 && (
        <div className="position-absolute z-1 inputtag-dropdown overflow-auto disabled">
          {filteredItems.map((item) => (
            <div
              key={item?.id}
              className="px-15px py-8px d-flex align-items-center hover-bg-off-white text-break"
              style={{ cursor: "pointer" }}
            >
              <div className="theme-check">
                <input
                  type="checkbox"
                  className="theme-check-input"
                  checked={selectedTags?.includes(`${item?.label} - ${item?.postcode}${item?.search_type === "manual" ? ' - M' : ''}`)}
                  id={item.id.toString()}
                  readOnly
                  onClick={() => toggleTag(`${item?.label} - ${item?.postcode}${item?.search_type === "manual" ? ' - M' : ''}`)}
                />
                <label
                  htmlFor={item.id.toString()}
                  className="theme-check-label lh-24px"
                >
                  {item?.label}
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
